import FundingService from '../../api/funding';
import payloadHelper from 'src/services/payloadHelper';
import Vue from 'vue';

// initial state
const state = {
  funderTableData: {
    funding: null,
    fundingPublication: null,
  },
  recipientTableData: {
    funding: null,
    fundingPublication: null,
  },
  fundingByType: {
    funding: null,
    fundingPublication: null,
  },
  fundingByTypeOverTime: {
    funding: null,
    fundingPublication: null,
  },
  choroplethData: {
    funding: null,
    fundingPublication: null,
  },
  pscCodeData: null, // Only exists for the funding source
  totalFundingCounts: {
    funding: null,
    fundingPublication: null,
  },
  filteredFundingCounts: {
    funding: null,
    fundingPublication: null,
  },
  loadState: {
    funding: {
      funderTableData: false,
      recipientTableData: false,
      fundingByType: false,
      fundingByTypeOverTime: false,
      choroplethData: false,
      pscCodeData: false,
      totalFundingCounts: false,
      filteredFundingCounts: false,
    },
    fundingPublication: {
      funderTableData: false,
      recipientTableData: false,
      fundingByType: false,
      fundingByTypeOverTime: false,
      choroplethData: false,
      fundingPublicationCounts: false,
      totalFundingCounts: false,
      filteredFundingCounts: false,
    },
  },
  error: null,
};

const getters = {
  funderTableData: (state) => {
    return state.funderTableData;
  },
  recipientTableData: (state) => {
    return state.recipientTableData;
  },
  fundingByType: (state) => {
    return state.fundingByType;
  },
  fundingByTypeOverTime: (state) => {
    return state.fundingByTypeOverTime;
  },
  choroplethData: (state) => {
    return state.choroplethData;
  },
  pscCodeData: (state) => {
    return state.pscCodeData;
  },
  totalFundingCounts: (state) => {
    return state.totalFundingCounts;
  },
  filteredFundingCounts: (state) => {
    return state.filteredFundingCounts;
  },
  loadState: (state) => {
    return state.loadState;
  },
  error: (state) => {
    return state.error;
  },
};

// Actions
const actions = {
  async loadFilters({ dispatch, rootGetters }, payload) {
    // Set source by what is included in the payload --OR-- whatever the activeDataSource is
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];

    // Create payload to pass to updateGlobalFilters
    const _payload = {
      source,
      createScanVersion: false,
    };
    // Call updateGlobalFilters in Filters module (pass in {root: true}) because that's where we want the global filter aggregation data stored. updateGlobalFilters will dispatch the necessary actions to update this store when filters are updated.
    dispatch('filters/updateGlobalFilters', _payload, { root: true });
  },
  async getTableData({ dispatch, commit, state, rootGetters }, payload) {
    const source = payload.source || rootGetters['app/activeDataSource'];
    commit('setLoadState', { source, type: 'funderTableData', value: false });
    commit('setLoadState', {
      source,
      type: 'recipientTableData',
      value: false,
    });

    const { query_builder, filters, scan_type, version_id } =
      payloadHelper.buildBasePayloadProps(rootGetters, source);
    const _payload = {
      scan_type,
      source,
      query: query_builder,
      filters,
      scan_id: version_id,
    };

    dispatch('getFunderTableData', _payload);
    dispatch('getRecipientTableData', _payload);
  },
  async getFunderTableData({ commit }, payload) {
    const fundingService = new FundingService();
    const response = await fundingService.getTableData({
      ...payload,
      tableType: 'funders',
    });

    if (response && response.success) {
      commit('setFunderTableData', {
        data: response.data,
        source: payload.source,
      });
      commit('setError', null);
    } else {
      commit('setFunderTableData', {
        data: [],
        source: payload.source,
      });
      commit('setError', 'Error getting funder table data');
    }
    commit('setLoadState', {
      source: payload.source,
      type: 'funderTableData',
      value: true,
    });
  },
  async getRecipientTableData({ commit }, payload) {
    const fundingService = new FundingService();
    const response = await fundingService.getTableData({
      ...payload,
      tableType: 'recipients',
    });

    if (response && response.success) {
      commit('setRecipientTableData', {
        data: response.data,
        source: payload.source,
      });
      commit('setError', null);
    } else {
      commit('setRecipientTableData', {
        data: [],
        source: payload.source,
      });
      commit('setError', 'Error getting recipient table data');
    }
    commit('setLoadState', {
      source: payload.source,
      type: 'recipientTableData',
      value: true,
    });
  },
  async getGraphData({ dispatch, commit, state, rootGetters }, payload) {
    const source = payload.source || rootGetters['app/activeDataSource'];
    commit('setLoadState', { source, type: 'fundingByType', value: false });
    commit('setLoadState', {
      source,
      type: 'fundingByTypeOverTime',
      value: false,
    });
    commit('setLoadState', { source, type: 'choroplethData', value: false });
    const { scan_type, query_builder, filters, version_id } =
      payloadHelper.buildBasePayloadProps(rootGetters, source);
    const _payload = {
      scan_type,
      source,
      query: query_builder,
      filters,
      scan_id: version_id,
    };
    dispatch('getFundingByType', _payload);
    dispatch('getFundingByTypeOverTime', _payload);
    dispatch('getChoroplethData', _payload);
    // Funding specific actions
    if (source === 'funding') {
      commit('setLoadState', { source, type: 'pscCodeData', value: false });
      // Only dispatch these actions for boolean scans
      if (scan_type !== 'semantic') {
        dispatch('getPscCodeData', _payload);
      }
    }
  },
  async getChoroplethData({ commit }, payload) {
    const fundingService = new FundingService();
    const response = await fundingService.getChoroplethData(payload);

    if (response && response.success) {
      commit('setChoroplethData', {
        source: payload.source,
        data: response.data,
      });
      commit('setError', null);
    } else {
      commit('setError', 'Error getting choropleth data');
    }
    commit('setLoadState', {
      source: payload.source,
      type: 'choroplethData',
      value: true,
    });
  },
  async getFundingByType({ commit }, payload) {
    const fundingService = new FundingService();
    const response = await fundingService.getFundingByType(payload);
    if (response && response.success) {
      commit('setFundingByType', {
        source: payload.source,
        data: response.data,
      });
      commit('setError', null);
    } else {
      commit('setError', 'Error getting bar chart data');
    }
    commit('setLoadState', {
      source: payload.source,
      type: 'fundingByType',
      value: true,
    });
  },
  async getFundingByTypeOverTime({ commit }, payload) {
    const fundingService = new FundingService();
    const response = await fundingService.getFundingByTypeOverTime(payload);
    if (response && response.success) {
      commit('setFundingByTypeOverTime', {
        source: payload.source,
        data: response.data,
      });
      commit('setError', null);
    } else {
      commit('setError', 'Error getting line chart data');
    }
    commit('setLoadState', {
      source: payload.source,
      type: 'fundingByTypeOverTime',
      value: true,
    });
  },
  async getPscCodeData({ commit }, payload) {
    const fundingService = new FundingService();
    const response = await fundingService.getPscCodeData(payload);
    if (response && response.success) {
      commit('setPscCodeData', response.data);
      commit('setError', null);
    } else {
      commit('setError', 'Error getting psc code data');
    }
    commit('setLoadState', {
      source: payload.source,
      type: 'pscCodeData',
      value: true,
    });
  },
  // Get doc counts and funding amount for the scan
  async getFundingCounts({ commit, rootGetters }, payload) {
    const source = payload.source || rootGetters['app/activeDataSource'];
    commit('setLoadState', {
      source,
      type: payload.type,
      value: false,
    });
    const { scan_type, query_builder, filters, version_id } =
      payloadHelper.buildBasePayloadProps(rootGetters, source);
    const _payload = {
      scan_type,
      query: query_builder,
      filters: payload.type === 'filteredFundingCounts' ? filters : null, // Only include filters if getting filtered counts
      scan_id: version_id,
      source,
    };
    const fundingService = new FundingService();
    const response = await fundingService.getFundingCounts(_payload);
    if (response && response.success) {
      commit('setFundingCounts', {
        source,
        data: response.data,
        type: payload.type,
      });
      commit('setError', null);
      commit('setLoadState', {
        source,
        type: payload.type,
        value: true,
      });
      return response.data;
    } else {
      commit('setError', 'Error getting funding counts');
      return null;
    }
  },
  clearStore({ commit }) {
    commit('setFunderTableData', null);
    commit('setRecipientTableData', null);
    commit('setFundingByType', null);
    commit('setFundingByTypeOverTime', null);
    commit('setChoroplethData', null);
    commit('setPscCodeData', null);
    commit('setFundingCounts', null);
    commit('setError', null);
  },
};

// Mutations
const mutations = {
  setFunderTableData(state, payload) {
    if (payload) {
      Vue.set(state.funderTableData, payload.source, payload.data);
    } else {
      state.funderTableData = {
        funding: null,
        fundingPublication: null,
      };
    }
  },
  setRecipientTableData(state, payload) {
    if (payload) {
      Vue.set(state.recipientTableData, payload.source, payload.data);
    } else {
      state.recipientTableData = {
        funding: null,
        fundingPublication: null,
      };
    }
  },
  setFundingByType(state, payload) {
    if (payload) {
      Vue.set(state.fundingByType, payload.source, payload.data);
    } else {
      state.fundingByType = {
        funding: null,
        fundingPublication: null,
      };
    }
  },
  setFundingByTypeOverTime(state, payload) {
    if (payload) {
      Vue.set(state.fundingByTypeOverTime, payload.source, payload.data);
    } else {
      state.fundingByTypeOverTime = {
        funding: null,
        fundingPublication: null,
      };
    }
  },
  setChoroplethData(state, payload) {
    if (payload) {
      Vue.set(state.choroplethData, payload.source, payload.data);
    } else {
      state.choroplethData = {
        funding: null,
        fundingPublication: null,
      };
    }
  },
  setPscCodeData(state, payload) {
    state.pscCodeData = payload;
  },
  setFundingCounts(state, payload) {
    if (payload) {
      Vue.set(state[payload.type], payload.source, payload.data);
    } else {
      state.totalFundingCounts = {
        funding: null,
        fundingPublication: null,
      };
      state.filteredFundingCounts = {
        funding: null,
        fundingPublication: null,
      };
    }
  },
  setLoadState(state, payload) {
    const { source, type, value } = payload;
    Vue.set(state.loadState[source], type, value);
  },
  setError(state, payload) {
    state.error = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
